import { Datasources, isDisplayOptionsOddsType } from '@adflow/types';
import { Divider, Heading, Select } from '@chakra-ui/react';
import { shallow } from 'zustand/shallow';
import {
  ActionMeta,
  GroupBase,
  Select as MultiSelect,
  MultiValue,
  SelectInstance,
  SingleValue,
} from 'chakra-react-select';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useStore } from '../../../hooks/useStore';
import { EditorStoreContext } from '../../..';
import Button from '../../ThemedButton';
import Box from '@mui/material/Box';
import { SourceConfig } from '..';
import OddsFormat from '../../Rightsidebar/TextElementControls/OddsControls/OddsFormat';
import {
  getSelectedStage,
  getStageMarketConfig,
  shouldShowOddsFormat,
  getInitialSelectedMarkets,
  filterSelectedOptionsForSave,
  elementReferencesDataSource,
  getOrphanNascarStageSourceIds,
  filterMarketOptionsForTemplate,
  getSelectedMarketOptions,
  isSportsEventTemplate,
} from './stageMarkets';

type Option = {
  label: string;
  value: Datasources.SourceType;
};

export default function SportsFormFields({
  firstFieldRef,
  onCancel,
  sources,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  firstFieldRef: React.RefObject<any>;
  onCancel: () => void;
  sources: SourceConfig[];
}) {
  const marketSelectRef = useRef<SelectInstance<Option, boolean, GroupBase<Option>>>(null);
  const stageMarketSelectRef = useRef<SelectInstance<Option, true, GroupBase<Option>>>(null);
  const ctx = useContext(EditorStoreContext);
  const s = useStore(
    (state) => ({
      elements: state.elements,
      dataSources: state.dataSources,
      displayOptions: state.displayOptions,
      setDisplayOptions: state.setDisplayOptions,
      addDataSource: state.addDataSource,
      removeDataSource: state.removeDataSource,
      removeElementDataSource: state.removeElementDataSource,
    }),
    shallow,
  );

  const [selectedOptions, addOption, removeOption, clearOptions] = useSet<Datasources.SourceType>(
    new Set(s.dataSources.map((s) => s.type)),
  );

  const [displayOptionsOdds, setDisplayOptionsOdds] = useState({
    type: 'decimalOdds' as const,
    ...s.displayOptions.odds,
  });

  const defaultTemplateOption = useMemo(
    () =>
      s.dataSources.find((ds) => Datasources.getDataTypeFromSourceType(ds.type) === 'TEMPLATE')
        ?.type,
    [s.dataSources],
  );

  // from the default template selected, get the default source
  const defaultSourceOption = useMemo(() => {
    if (defaultTemplateOption) {
      const source =
        sources.find((s) => s.templateTypes.includes(defaultTemplateOption)) || sources[0];
      return sources.find((item) => item.id === source.id) || sources[0];
    } else {
      return sources[0];
    }
  }, [defaultTemplateOption, sources]);

  const [currentSource, setCurrentSource] = useState(defaultSourceOption);

  const findTemplateFromSelectedOptions = useCallback((): Datasources.SourceType | undefined => {
    return Array.from(selectedOptions).find(
      (option) => Datasources.getDataTypeFromSourceType(option) === 'TEMPLATE',
    );
  }, [selectedOptions]);

  const hasSelectedTemplate = useMemo(() => {
    return (
      selectedOptions.size > 0 &&
      Array.from(selectedOptions).some(
        (option) => Datasources.getDataTypeFromSourceType(option) === 'TEMPLATE',
      )
    );
  }, [selectedOptions]);

  const isSourceDirectWithCompetitions = useMemo(() => {
    return ['SPORTS_MOMENTS', 'CAR_RACING'].includes(currentSource.id);
  }, [currentSource]);

  const templateOptions = useMemo(() => {
    return ctx.dataSources.filter(
      (source) =>
        source.dataType === 'TEMPLATE' &&
        source.type !== 'EVENT' &&
        currentSource.templateTypes.includes(source.type),
    );
  }, [ctx.dataSources, currentSource.templateTypes]);

  const marketOptions = useMemo(() => {
    const marketSources = ctx.dataSources.filter(
      (source) => source.dataType === 'MARKET' && currentSource.marketTypes.includes(source.type),
    );

    // First Goalscorer is only available for the Sports/Event combination.
    let options = filterMarketOptionsForTemplate(
      marketSources.map((source) => ({
        value: Datasources.SourceTypeSchema.parse(source.type),
        label: Datasources.getFriendlySourceNameFromSourceType(source.type),
      })),
      currentSource.id,
      findTemplateFromSelectedOptions(),
    );

    // Filter market sources to only include the stages of the selected competition(s)
    if (isSourceDirectWithCompetitions) {
      const selectedTemplate = findTemplateFromSelectedOptions();
      if (selectedTemplate) {
        const availableMarketTypes =
          Datasources.availableMarketTypesForCompetition(selectedTemplate);
        options = options.filter((availableMarket) =>
          availableMarketTypes.includes(availableMarket.value),
        );
      }
    }

    return options;
  }, [
    ctx.dataSources,
    currentSource.marketTypes,
    findTemplateFromSelectedOptions,
    isSourceDirectWithCompetitions,
    currentSource.id,
  ]);

  const selectedMarketOptions = useMemo(() => {
    return getSelectedMarketOptions(marketOptions, selectedOptions);
  }, [marketOptions, selectedOptions]);

  const selectedStage = useMemo(
    () => getSelectedStage(selectedOptions, currentSource.marketTypes),
    [selectedOptions, currentSource.marketTypes],
  );

  const stageMarketConfig = useMemo(
    () => getStageMarketConfig(selectedStage, currentSource.stageMarkets),
    [selectedStage, currentSource.stageMarkets],
  );

  const [selectedMarkets, setSelectedMarkets] = useState<Datasources.SourceType[]>(() =>
    getInitialSelectedMarkets(s.dataSources, currentSource.stageMarkets),
  );

  const stageMarketOptions = useMemo(() => {
    if (!stageMarketConfig) return [];
    return stageMarketConfig.availableMarkets.map((market) => ({
      value: market,
      label: Datasources.getFriendlySourceNameFromSourceType(market),
    }));
  }, [stageMarketConfig]);

  const selectedStageMarketOptions = useMemo(
    () => stageMarketOptions.filter((opt) => selectedMarkets.includes(opt.value)),
    [stageMarketOptions, selectedMarkets],
  );

  const clearSelectedStageMarkets = useCallback(() => {
    selectedMarkets.forEach((market) => removeOption(market));
    setSelectedMarkets([]);
    stageMarketSelectRef.current?.clearValue();
  }, [selectedMarkets, removeOption]);

  const handleChangeTemplate: React.ChangeEventHandler<HTMLSelectElement> = useCallback(
    (evt) => {
      const newVal = evt.target.value;
      selectedOptions.forEach((option) => {
        if (Datasources.getDataTypeFromSourceType(option) === 'TEMPLATE' && option !== newVal) {
          removeOption(option);
        }
      });

      if (isSourceDirectWithCompetitions) {
        selectedOptions.clear();
        marketSelectRef.current?.clearValue();
        clearSelectedStageMarkets();
      }

      if (newVal === '' || newVal == null) {
        return;
      }
      const parsedVal = Datasources.SourceTypeSchema.parse(newVal);
      addOption(parsedVal);

      if (!isSportsEventTemplate(currentSource.id, parsedVal)) {
        selectedOptions.forEach((option) => {
          if (option === 'FIRST_GOALSCORER') removeOption(option);
        });
      }
    },
    [
      addOption,
      clearSelectedStageMarkets,
      currentSource.id,
      isSourceDirectWithCompetitions,
      removeOption,
      selectedOptions,
    ],
  );

  const handleChangeMarket = useCallback(
    (values: MultiValue<Option> | SingleValue<Option>, actionType: ActionMeta<Option>) => {
      clearSelectedStageMarkets();

      if (actionType.action === 'remove-value' || actionType.action === 'pop-value') {
        removeOption(actionType.removedValue.value);
      }
      const singleValue = (values as SingleValue<Option>)?.value;
      if (actionType.action === 'select-option' && actionType.option) {
        addOption(actionType.option.value);
      }
      // when the selection is a SingleValue<Option> / single market type
      else if (actionType.action === 'select-option' && singleValue) {
        // logic to enable only one market type selected
        // remove all other market types
        selectedOptions.forEach((option) => {
          if (!templateOptions.find((t) => t.type === option)) removeOption(option);
        });
        addOption(singleValue);
      }

      if (actionType.action === 'clear') {
        actionType.removedValues.forEach((option) => {
          removeOption(option.value);
        });
      }
    },
    [addOption, clearSelectedStageMarkets, removeOption, selectedOptions, templateOptions],
  );

  const handleChangeStageMarket = useCallback(
    (values: MultiValue<Option>, actionType: ActionMeta<Option>) => {
      if (actionType.action === 'remove-value' || actionType.action === 'pop-value') {
        removeOption(actionType.removedValue.value);
        setSelectedMarkets((prev) =>
          prev.filter((market) => market !== actionType.removedValue.value),
        );
        return;
      }

      if (actionType.action === 'clear') {
        actionType.removedValues.forEach((option) => {
          removeOption(option.value);
        });
        setSelectedMarkets([]);
        return;
      }

      if (actionType.action === 'select-option' && actionType.option) {
        const market = actionType.option.value;
        addOption(market);
        setSelectedMarkets((prev) => (prev.includes(market) ? prev : [...prev, market]));
        return;
      }

      // Fallback: sync from values array
      const nextMarkets = values.map((v) => v.value);
      selectedMarkets.forEach((market) => {
        if (!nextMarkets.includes(market)) removeOption(market);
      });
      nextMarkets.forEach((market) => {
        if (!selectedMarkets.includes(market)) addOption(market);
      });
      setSelectedMarkets(nextMarkets);
    },
    [addOption, removeOption, selectedMarkets],
  );

  const handleChangeOddsFormat: React.ChangeEventHandler<HTMLSelectElement> = useCallback((evt) => {
    const oddsFormat = evt.target.value;
    if (isDisplayOptionsOddsType(oddsFormat)) {
      setDisplayOptionsOdds((prevVal) => {
        if (oddsFormat === 'potentialWinnings') {
          return { ...prevVal, type: oddsFormat, stake: 10, currency: 'GBP' };
        }
        return { ...prevVal, type: oddsFormat, stake: null, currency: null };
      });
    } else {
      setDisplayOptionsOdds((prevVal) => ({
        ...prevVal,
        type: 'decimalOdds',
        stake: null,
        currency: null,
      }));
    }
  }, []);

  const setOddsStakeCurrency: React.ChangeEventHandler<HTMLSelectElement> = useCallback((evt) => {
    const currency = evt.target.value;
    setDisplayOptionsOdds((prevVal) => ({ ...prevVal, currency }));
  }, []);

  const setOddsStakeAmount: React.ChangeEventHandler<HTMLInputElement> = useCallback((evt) => {
    const stake = parseInt(evt.target.value);
    if (!isNaN(stake)) {
      setDisplayOptionsOdds((prevVal) => ({ ...prevVal, stake }));
    }
  }, []);

  const setCalculateDecimalPlaces = useCallback((isChecked: boolean) => {
    setDisplayOptionsOdds((prevVal) => ({ ...prevVal, calculateDecimalPlaces: isChecked }));
  }, []);

  // Returns the static ID of a data source, if it exists
  const findIDofDatasource = (type: Datasources.SourceType): string | undefined => {
    return (ctx.dataSources.find((ds) => ds.type === type) || {}).id;
  };

  const onSave = () => {
    const selectedOptionsArray = filterSelectedOptionsForSave(
      Array.from(selectedOptions),
      selectedMarkets,
      currentSource.stageMarkets,
    );

    selectedOptionsArray.forEach((option) => {
      const dataSourceExists = s.dataSources.some((dataSource) => dataSource.type === option);
      if (!dataSourceExists) {
        s.addDataSource(
          option,
          Datasources.getDataTypeFromSourceType(option),
          findIDofDatasource(option),
        );
      }
    });

    // NASCAR import without a confirmed stage still binds layers to an arbitrary
    // sibling stage catalog ID that was never added to the template.
    // Treat those like a removed data source so content becomes DATA_SOURCE_REMOVED.
    if (currentSource.id === 'CAR_RACING') {
      const selectedStageType = getSelectedStage(
        new Set(selectedOptionsArray),
        currentSource.marketTypes,
      );
      if (selectedStageType) {
        const orphanIds = getOrphanNascarStageSourceIds(ctx.dataSources, selectedStageType);
        s.elements.forEach((el) => {
          orphanIds.forEach((dataSourceId) => {
            if (elementReferencesDataSource(el, dataSourceId)) {
              s.removeElementDataSource(el.id, dataSourceId, { skipHistory: true });
            }
          });
        });
      }
    }

    // Remove any data sources that are not selected
    s.dataSources.forEach((dataSource) => {
      const optionExists = selectedOptionsArray.includes(dataSource.type);
      if (!optionExists) {
        s.removeDataSource(dataSource.id);
        // cleanup any data sources from elements
        s.elements.forEach((el) => {
          s.removeElementDataSource(el.id, dataSource.id, { skipHistory: true });
        });
      }
    });
    // Data source setup is not undoable, so the odds format chosen with it is not either.
    if (displayOptionsOdds) {
      s.setDisplayOptions('odds', displayOptionsOdds, { skipHistory: true });
    }
    onCancel();
  };

  const handleSourceChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const selectedSourceId = event.target.value;

      setCurrentSource(sources.find((source) => source.id === selectedSourceId) ?? sources[0]);
      handleChangeTemplate({
        target: { value: '' },
      } as React.ChangeEvent<HTMLSelectElement>);
      // remove from the store and from the useSet
      s.dataSources.forEach(({ id }) => s.removeDataSource(id));
      clearOptions();
      setSelectedMarkets([]);
      marketSelectRef.current?.setValue([], 'deselect-option');
      stageMarketSelectRef.current?.clearValue();
    },
    [sources, handleChangeTemplate, s, clearOptions],
  );
  const hasMarkets = useMemo(
    () => currentSource.marketTypes.length > 0,
    [currentSource.marketTypes],
  );

  return (
    <>
      <Heading size="sm">Source</Heading>
      <Select
        defaultValue={currentSource.id}
        data-test-id="data-processors"
        onChange={handleSourceChange}
      >
        {sources.map((ds) => {
          return (
            <option key={ds.name} value={ds.id}>
              {ds.name}
            </option>
          );
        })}
      </Select>
      <Divider />
      <Heading size="sm">
        {currentSource.id === 'SPORTS_MOMENTS'
          ? 'Sport'
          : currentSource.id === 'CAR_RACING'
          ? 'Competition'
          : 'Template Type'}
      </Heading>
      <Select
        data-test-id="select-datasource"
        ref={firstFieldRef}
        placeholder={
          currentSource.id === 'SPORTS_MOMENTS'
            ? 'Select Sport'
            : currentSource.id === 'CAR_RACING'
            ? 'Select Competition'
            : 'Select Template Type'
        }
        onChange={handleChangeTemplate}
        defaultValue={defaultTemplateOption}
      >
        {templateOptions.map((ds) => (
          <option key={ds.name} value={ds.type}>
            {ds.name}
          </option>
        ))}
      </Select>
      {hasMarkets && (!isSourceDirectWithCompetitions || hasSelectedTemplate) && (
        <>
          <Divider />
          <Heading size="sm">{isSourceDirectWithCompetitions ? 'Stage' : 'Market types'}</Heading>
          <MultiSelect
            ref={marketSelectRef}
            isMulti={currentSource.multipleMarkets}
            placeholder={isSourceDirectWithCompetitions ? 'Select Stage' : 'Select Market Types'}
            options={marketOptions}
            onChange={handleChangeMarket}
            value={selectedMarketOptions}
          ></MultiSelect>
        </>
      )}
      {stageMarketConfig && (
        <>
          <Divider />
          <Heading size="sm">Market</Heading>
          <MultiSelect
            ref={stageMarketSelectRef}
            isMulti
            data-test-id="select-stage-market"
            placeholder="Select Market (optional)"
            options={stageMarketOptions}
            value={selectedStageMarketOptions}
            onChange={handleChangeStageMarket}
          />
        </>
      )}
      {shouldShowOddsFormat(
        currentSource.needsOddsFormatting,
        stageMarketConfig,
        selectedMarkets,
      ) ? (
        <OddsFormat
          oddsDisplayOptions={displayOptionsOdds}
          handleChangeOddsFormat={handleChangeOddsFormat}
          setOddsStakeAmount={setOddsStakeAmount}
          setOddsStakeCurrency={setOddsStakeCurrency}
          setCalculateDecimalPlaces={setCalculateDecimalPlaces}
        />
      ) : null}
      <Box display={'flex'} justifyContent={'flex-end'} gap={'1rem'}>
        <Button muiButtonProps={{ color: 'secondary', onClick: onCancel }}>Cancel</Button>
        <Button testId="save-button" muiButtonProps={{ color: 'primary', onClick: onSave }}>
          Save
        </Button>
      </Box>
    </>
  );
}

function useSet<T>(
  initialSet: Set<T> = new Set(),
): [Set<T>, (value: T) => void, (value: T) => void, () => void] {
  const [set, setSet] = useState(initialSet);

  const add = useCallback((value: T) => {
    setSet((prevSet) => new Set(prevSet).add(value));
  }, []);

  const remove = useCallback((value: T) => {
    setSet((prevSet) => {
      const newSet = new Set(prevSet);
      newSet.delete(value);
      return newSet;
    });
  }, []);

  const clear = useCallback(() => {
    setSet((prevSet) => {
      const newSet = new Set(prevSet);
      newSet.clear();
      return newSet;
    });
  }, []);

  return [set, add, remove, clear];
}
